import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, BACKEND_URL } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dumbbell, Newspaper, Trophy, Camera, Crown, Target, Users, Cake, Puzzle, Heart } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

const ACTIVITY_ICONS = {
  training_join: { icon: Dumbbell, color: 'text-blue-400', bg: 'bg-blue-900/30' },
  news_created: { icon: Newspaper, color: 'text-purple-400', bg: 'bg-purple-900/30' },
  badge_earned: { icon: Trophy, color: 'text-yellow-400', bg: 'bg-yellow-900/30' },
  photo_upload: { icon: Camera, color: 'text-green-400', bg: 'bg-green-900/30' },
  record_broken: { icon: Target, color: 'text-red-400', bg: 'bg-red-900/30' },
  new_pro: { icon: Crown, color: 'text-gold', bg: 'bg-yellow-600/20' },
  nuevo_miembro: { icon: Heart, color: 'text-pink-400', bg: 'bg-pink-900/30' },
  cumpleanos: { icon: Cake, color: 'text-pink-300', bg: 'bg-pink-900/30' },
  acertijo_publicado: { icon: Puzzle, color: 'text-purple-300', bg: 'bg-purple-900/40' },
  default: { icon: Users, color: 'text-gray-400', bg: 'bg-gray-800' }
};

function ActivityFeed() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchActivities = async () => {
    try {
      const res = await axios.get(`${API}/activity`);
      setActivities(res.data);
    } catch (error) {
      console.error("Error fetching activity feed:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivities();
    // Poll every 30s
    const interval = setInterval(fetchActivities, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading && activities.length === 0) {
    return <div className="h-40 flex items-center justify-center text-gray-500">Cargando actividad...</div>;
  }

  if (activities.length === 0) {
    return (
      <div className="h-20 flex items-center justify-center text-gray-500 text-sm bg-gray-800/30 rounded-lg">
        No hay actividad reciente
      </div>
    );
  }

  return (
    <Card className="bg-gray-900 border-gray-800 shadow-lg">
      <CardHeader className="pb-2">
        <CardTitle className="text-gold text-lg flex items-center gap-2">
          <Users className="h-5 w-5" /> Muro del Klan
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[300px] pr-4">
          <div className="space-y-4">
            {activities.map((item) => {
              const style = ACTIVITY_ICONS[item.type] || ACTIVITY_ICONS.default;
              const Icon = style.icon;
              
              return (
                <div key={item.id} className="flex gap-3 items-start">
                  {/* Avatar / Icon */}
                  <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${style.bg} border border-white/10 mt-1`}>
                    {item.user_avatar ? (
                        <img 
                            src={`${BACKEND_URL}${item.user_avatar}`} 
                            alt={item.user_name} 
                            className="w-full h-full rounded-full object-cover"
                        />
                    ) : (
                        <Icon className={`h-4 w-4 ${style.color}`} />
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 text-sm">
                    <p className="text-gray-200">
                      <span className="font-bold text-white">{item.user_name}</span>{' '}
                      {item.details}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {formatDistanceToNow(new Date(item.timestamp), { addSuffix: true, locale: es })}
                    </p>
                    
                    {/* Metadata Extras (Images, etc) */}
                    {item.metadata?.image && (
                        <div className="mt-2 rounded-lg overflow-hidden h-24 w-full max-w-[200px] border border-gray-700">
                            <img 
                                src={`${BACKEND_URL}${item.metadata.image}`} 
                                alt="Activity" 
                                className="w-full h-full object-cover"
                            />
                        </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}

export default ActivityFeed;
